import { useCallback, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AgGridReact } from 'ag-grid-react';
import { CellStyle, ColDef, ICellRendererParams } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { OnTopCostWithRelations } from '../../services/budgetOnTopCost';
import { PendingCellEdits } from '../../hooks/usePendingCellEdits';
import {
    useUpdateOnTopCost,
    useCreateOnTopCost,
    useDeleteOnTopCost,
    useDownloadOnTopCostsImportTemplate,
    useExportOnTopCosts,
} from '../../hooks/api/budgetOnTopCost';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import OnTopCostsImportDialog from './OnTopCostsImportDialog';
import ImportSplitButton from './ImportSplitButton';
import MonthWindowNav from './MonthWindowNav';
import { generateMonthColumns, currencyFormatter, parseBudgetCell } from './helpers';
import { useOverviewMonths } from './useOverviewMonths';
import { pendingBorder } from './pendingCellStyle';

/** The pending kind this grid's month cells use — also the batch payload's field name. */
export const ON_TOP_VALUE_KIND = 'valueEdits';

/** Every kind this grid owns. The Overview's store is shared with the Handlingdaten and Ist-Budget
 *  grids, whose row ids are independent sequences, so anything acting on "this row's edits" has to say
 *  which resource it means. */
export const ON_TOP_EDIT_KINDS = ['field', ON_TOP_VALUE_KIND];

/** The metadata columns a cell edit may write: the `fields` object of the batch payload. A whitelist,
 *  so a read-only column can never be posted. */
const EDITABLE_FIELDS = [
    'contactPerson',
    'owner',
    'costCenter',
    'prName',
    'category',
    'description',
    'raittFlag',
    'worktypeLabel',
    'channelLabel',
    'supplier',
    'location',
    'priceCategory',
    'forecasterWfm',
    'headOfBudget',
    'prPm',
    'includeInBudget',
];

interface OnTopCostsGridProps {
    onTopCosts: OnTopCostWithRelations[];
    isLoading: boolean;
    versionId: number;
    /** The Overview's pending edits — this grid records into them; the page saves them (CALL-779). */
    pending: PendingCellEdits;
    /** CALL-781: hide month COLUMNS before the current month. Display only. */
    hidePastMonths?: boolean;
}

type DialogMode = 'create' | 'edit' | null;

interface DialogState {
    mode: DialogMode;
    id: number | null;
    contactPerson: string;
    owner: string;
    costCenter: string;
    prName: string;
    category: string;
    description: string;
    raittFlag: boolean;
    worktypeLabel: string;
    channelLabel: string;
    supplier: string;
    location: string;
    priceCategory: string;
    forecasterWfm: string;
    headOfBudget: string;
    prPm: boolean;
    includeInBudget: boolean;
}

const emptyDialog: DialogState = {
    mode: null,
    id: null,
    contactPerson: '',
    owner: '',
    costCenter: '',
    prName: '',
    category: '',
    description: '',
    raittFlag: false,
    worktypeLabel: '',
    channelLabel: '',
    supplier: '',
    location: '',
    priceCategory: '',
    forecasterWfm: '',
    headOfBudget: '',
    prPm: false,
    includeInBudget: true,
};

const booleanCellRenderer = (propertyName: string) => (params: ICellRendererParams) => {
    if (!params.data) return null;
    return <Checkbox checked={!!params.data[propertyName]} disabled style={{ padding: 0 }} />;
};

const parseBooleanValue = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    const str = String(value).toLowerCase().trim();
    return ['ja', 'true', '1', 'yes'].includes(str);
};

const OnTopCostsGrid = ({ onTopCosts, isLoading, versionId, pending, hidePastMonths = false }: OnTopCostsGridProps) => {
    const { t } = useTranslation('budget');
    const gridRef = useRef<AgGridReact>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [dialog, setDialog] = useState<DialogState>(emptyDialog);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const { mutate: downloadTemplate, isLoading: isDownloadingTemplate } = useDownloadOnTopCostsImportTemplate();
    const { mutate: exportCosts, isLoading: isExporting } = useExportOnTopCosts(versionId);
    const { isReadOnly } = useBudgetContext();

    const resetDialog = useCallback(() => setDialog(emptyDialog), []);

    // CALL-779: the single-cost PATCH is the create/edit DIALOG's path now; a cell edit is recorded in
    // the page's pending store and written by its Save.
    const { mutate: updateCost } = useUpdateOnTopCost(versionId);
    const { mutate: createCost } = useCreateOnTopCost(versionId, { onSettled: resetDialog });
    const { mutate: deleteCost } = useDeleteOnTopCost(versionId);

    // Served values per cost — the row build's base and the pristine value an edit is measured against.
    // Month keys are bare here; this grid's columns prefix them with `month_`.
    const servedById = useMemo(() => new Map(onTopCosts.map((cost) => [cost.id, cost])), [onTopCosts]);
    const servedValues = useMemo(
        () =>
            new Map(
                onTopCosts.map((cost) => [
                    cost.id,
                    new Map((cost.values || []).map((value) => [String(value.month), Number(value.amount)])),
                ])
            ),
        [onTopCosts]
    );

    // This grid's slice of the pending set: fields and months, per cost.
    const pendingById = useMemo(() => {
        const byCost = new Map<number, { fields: Map<string, unknown>; months: Map<string, number | null> }>();

        for (const edit of pending.edits) {
            if (edit.kind !== 'field' && edit.kind !== ON_TOP_VALUE_KIND) continue;
            const entry = byCost.get(edit.rowId) ?? { fields: new Map(), months: new Map() };
            if (edit.kind === 'field') entry.fields.set(edit.key, edit.value);
            else entry.months.set(edit.key, edit.value as number | null);
            byCost.set(edit.rowId, entry);
        }

        return byCost;
    }, [pending.edits]);

    // CALL-781: `visibleMonths` is the column list with the past months dropped. This grid renders its
    // values straight from the cost records, so unlike the other two Overview grids it needs nothing
    // else. CALL-815: the underlying view is the 12-month window, or the selected date range if set.
    const { visibleMonths } = useOverviewMonths(hidePastMonths);

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(visibleMonths, {
                editable: !isReadOnly,
                // CALL-813 rounded the Overview's computed euro TOTALS to whole euros and deliberately
                // left these alone: a monthly on-top amount is an EDITABLE entered figure (see
                // `editable` above), not a sum, and what an editable cell shows has to paste back into
                // it unchanged — "1.235 €" retyped over a stored 1234,56 loses the cents (CALL-794/796).
                valueFormatter: (params) => {
                    if (params.value == null || params.value === '') return '';
                    return currencyFormatter.format(Number(params.value));
                },
                valueSetter: (params) => {
                    // CALL-744 #12: parse German-formatted input ("12,50", "1.234,56", "1.000 €") the
                    // same way every other budget grid and the Excel import do. Bare Number() returned
                    // NaN for a decimal comma and silently rejected the edit.
                    if (params.newValue === '' || params.newValue == null) {
                        params.data[params.colDef.field!] = null;
                        return true;
                    }
                    const parsed = parseBudgetCell(params.newValue);
                    if (parsed == null) return false;
                    params.data[params.colDef.field!] = parsed;
                    return true;
                },
                ...(!isReadOnly && {
                    cellStyle: (params): Record<string, string> => {
                        const val = params.value;
                        if (val != null && val !== '' && isNaN(Number(val))) {
                            return { backgroundColor: '#ffebee', border: '1px solid #ef5350' };
                        }
                        // CALL-779: an unsaved month gains the red border every Budget grid uses.
                        const month = (params.colDef?.field ?? '').replace('month_', '');
                        const rowId = params.data?.id != null ? Number(params.data.id) : null;
                        return {
                            backgroundColor: '#fffde7',
                            ...pendingBorder(Boolean(rowId != null && pendingById.get(rowId)?.months.has(month))),
                        };
                    },
                }),
            }).map((col) => ({
                ...col,
                // On-top costs use month_ prefix for field keys to avoid clashes with metadata
                field: `month_${col.field}`,
            })),
        // pendingById is read by cellStyle above: without it here the columns kept the map from the
        // first render, so the red unsaved-cell border never appeared on a month cell.
        [visibleMonths, isReadOnly, pendingById]
    );

    const rowData = useMemo(
        () =>
            onTopCosts.map((cost) => {
                const row: Record<string, unknown> = {
                    id: cost.id,
                    contactPerson: cost.contactPerson,
                    owner: cost.owner,
                    costCenter: cost.costCenter,
                    prName: cost.prName,
                    category: cost.category,
                    description: cost.description,
                    raittFlag: cost.raittFlag,
                    worktypeLabel: cost.worktypeLabel ?? '',
                    channelLabel: cost.channelLabel ?? '',
                    supplier: cost.supplier,
                    location: cost.location,
                    priceCategory: cost.priceCategory,
                    forecasterWfm: cost.forecasterWfm,
                    headOfBudget: cost.headOfBudget,
                    prPm: cost.prPm,
                    includeInBudget: cost.includeInBudget,
                };

                if (cost.values) {
                    for (const v of cost.values) {
                        row[`month_${v.month}`] = v.amount;
                    }
                }

                // CALL-779: pending edits sit on top of the served row, so an unsaved cell survives the
                // refetch that follows any other save on the page — ag-grid's own row copy does not.
                const pendingForCost = pendingById.get(cost.id);
                if (pendingForCost) {
                    for (const [field, value] of pendingForCost.fields) row[field] = value;
                    for (const [month, value] of pendingForCost.months) {
                        if (value === null) delete row[`month_${month}`];
                        else row[`month_${month}`] = Number(value);
                    }
                }

                return row;
            }),
        [onTopCosts, pendingById]
    );

    // CALL-779: record the cell for the page's Save. A pasted cell takes exactly the same route as a
    // typed one, which is what retires the paste bookkeeping this grid used to need: with autosave, one
    // paste across a row meant one request per cell, so pasted ids were collected in a ref and flushed
    // as whole-row writes in onPasteEnd. A pending store makes a paste an ordinary set of edits.
    const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef } = event;
            if (!data?.id || !colDef.field) return;

            const costId = Number(data.id);
            const field = colDef.field;

            if (field.startsWith('month_')) {
                const month = field.replace('month_', '');
                const value = data[field] == null ? null : Number(data[field]);
                pending.set(costId, ON_TOP_VALUE_KIND, month, value, servedValues.get(costId)?.get(month));
                return;
            }

            if (!EDITABLE_FIELDS.includes(field)) return;
            const served = servedById.get(costId) as unknown as Record<string, unknown> | undefined;
            pending.set(costId, 'field', field, data[field], served?.[field]);
        },
        [pending, servedById, servedValues]
    );

    const openCreateDialog = useCallback(() => {
        setDialog({ ...emptyDialog, mode: 'create' });
    }, []);

    const openEditDialog = useCallback(
        (id: number) => {
            const served = onTopCosts.find((c) => c.id === id);
            if (!served) return;
            // CALL-779: seeded from the EFFECTIVE row — the served values with this row's pending edits on
            // top. Reading the served row alone put the PRE-edit text in the form, so saving the dialog
            // wrote the old value back and the still-pending edit re-overlaid it on the fresh server row.
            const cost = { ...served, ...Object.fromEntries(pendingById.get(id)?.fields ?? []) } as typeof served;
            setDialog({
                mode: 'edit',
                id,
                contactPerson: cost.contactPerson ?? '',
                owner: cost.owner ?? '',
                costCenter: cost.costCenter ?? '',
                prName: cost.prName ?? '',
                category: cost.category ?? '',
                description: cost.description ?? '',
                raittFlag: cost.raittFlag ?? false,
                worktypeLabel: cost.worktypeLabel ?? '',
                channelLabel: cost.channelLabel ?? '',
                supplier: cost.supplier ?? '',
                location: cost.location ?? '',
                priceCategory: cost.priceCategory ?? '',
                forecasterWfm: cost.forecasterWfm ?? '',
                headOfBudget: cost.headOfBudget ?? '',
                prPm: cost.prPm ?? false,
                includeInBudget: cost.includeInBudget ?? true,
            });
        },
        [onTopCosts, pendingById]
    );

    const handleDialogConfirm = useCallback(() => {
        // CALL-744 #5: send explicit null (not undefined) for cleared optional fields. An undefined
        // value is dropped from the JSON body, so the backend PATCH treats the absent key as "no
        // change" and the field can never be cleared. The schema accepts null for all of these.
        const payload = {
            contactPerson: dialog.contactPerson || null,
            owner: dialog.owner || null,
            costCenter: dialog.costCenter || null,
            prName: dialog.prName || null,
            category: dialog.category || null,
            description: dialog.description || null,
            raittFlag: dialog.raittFlag,
            worktypeLabel: dialog.worktypeLabel || null,
            channelLabel: dialog.channelLabel || null,
            supplier: dialog.supplier || null,
            location: dialog.location || null,
            priceCategory: dialog.priceCategory || null,
            forecasterWfm: dialog.forecasterWfm || null,
            headOfBudget: dialog.headOfBudget || null,
            prPm: dialog.prPm,
            includeInBudget: dialog.includeInBudget,
        };

        if (dialog.mode === 'create') {
            createCost(payload);
        } else if (dialog.mode === 'edit' && dialog.id) {
            const id = dialog.id;
            updateCost({ id, ...payload } as { id: number }, {
                // The dialog has just written every metadata column, so this row's pending field edits are
                // spent — left in place they would be posted again by the next Save, over the dialog's.
                onSuccess: () => pending.dropRow(id, ['field']),
                onSettled: resetDialog,
            });
        }
    }, [dialog, createCost, updateCost, resetDialog, pending]);

    const updateDialogField = useCallback(<K extends keyof DialogState>(field: K, value: DialogState[K]) => {
        setDialog((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleDeleteRow = useCallback((id: number) => {
        setDeleteConfirmId(id);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        if (deleteConfirmId !== null) {
            // CALL-779: forget the row's pending cells once the row is actually gone, or a later Save
            // would try to write a cost that no longer exists. Only ONCE THE DELETE LANDS, and only this
            // grid's kinds: dropping them next to the fire-and-forget mutation threw the edits away even
            // when the delete failed, and an unscoped drop took the Handlingdaten edits of the billing
            // config with the same id.
            deleteCost(deleteConfirmId, {
                onSuccess: () => pending.dropRow(deleteConfirmId, ON_TOP_EDIT_KINDS),
            });
            setDeleteConfirmId(null);
        }
    }, [deleteCost, deleteConfirmId, pending]);

    // CALL-779: a metadata cell is marked exactly like a month cell. Without this an unsaved text or
    // boolean edit was invisible while the bar counted it, so "Verwerfen" threw away changes the user had
    // no way to find.
    const fieldStyle = useCallback(
        (params: { colDef?: { field?: string }; data?: { id?: number } }): CellStyle => {
            const field = params.colDef?.field;
            const rowId = params.data?.id != null ? Number(params.data.id) : null;
            return pendingBorder(Boolean(field && rowId != null && pendingById.get(rowId)?.fields.has(field)));
        },
        [pendingById]
    );

    const metadataColumns: ColDef[] = useMemo(
        () => [
            {
                headerName: t('hint.contactList'),
                field: 'contactPerson',
                minWidth: 170,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.responsibleOwner'),
                field: 'owner',
                minWidth: 160,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.kst'),
                field: 'costCenter',
                minWidth: 100,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.prName'),
                field: 'prName',
                minWidth: 140,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.category'),
                field: 'category',
                minWidth: 120,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.description'),
                field: 'description',
                minWidth: 160,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.raittCssOpex'),
                field: 'raittFlag',
                minWidth: 100,
                cellRenderer: booleanCellRenderer('raittFlag'),
                editable: !isReadOnly,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: [true, false],
                    cellRenderer: (params: ICellRendererParams): string =>
                        params.value ? t('common.yes') : t('common.no'),
                },
                valueSetter: (params) => {
                    params.data.raittFlag = parseBooleanValue(params.newValue);
                    return true;
                },
                cellStyle: fieldStyle,
            },
            // CALL-757: worktype/channel are free text (no catalog link) — plain editable columns.
            {
                headerName: t('field.worktype'),
                field: 'worktypeLabel',
                minWidth: 140,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.channel'),
                field: 'channelLabel',
                minWidth: 120,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.supplierComment'),
                field: 'supplier',
                minWidth: 150,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.location'),
                field: 'location',
                minWidth: 120,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.priceCategory'),
                field: 'priceCategory',
                minWidth: 170,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.forecasterWfm'),
                field: 'forecasterWfm',
                minWidth: 130,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.headOfBudget'),
                field: 'headOfBudget',
                minWidth: 130,
                editable: !isReadOnly,
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.prPm'),
                field: 'prPm',
                minWidth: 80,
                cellRenderer: booleanCellRenderer('prPm'),
                editable: !isReadOnly,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: [true, false],
                    cellRenderer: (params: ICellRendererParams): string =>
                        params.value ? t('common.yes') : t('common.no'),
                },
                valueSetter: (params) => {
                    params.data.prPm = parseBooleanValue(params.newValue);
                    return true;
                },
                cellStyle: fieldStyle,
            },
            {
                headerName: t('field.includeInBudgetRequirement'),
                field: 'includeInBudget',
                minWidth: 200,
                cellRenderer: booleanCellRenderer('includeInBudget'),
                editable: !isReadOnly,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: [true, false],
                    cellRenderer: (params: ICellRendererParams): string =>
                        params.value ? t('common.yes') : t('common.no'),
                },
                valueSetter: (params) => {
                    params.data.includeInBudget = parseBooleanValue(params.newValue);
                    return true;
                },
                cellStyle: (params) => ({
                    ...(params.value === true ? { backgroundColor: '#c8e6c9' } : {}),
                    ...(params.value === false ? { backgroundColor: '#fff9c4' } : {}),
                    ...fieldStyle(params),
                }),
            },
        ],
        [isReadOnly, t, fieldStyle]
    );

    const actionColumn: ColDef = useMemo(
        () => ({
            headerName: '',
            field: 'actions',
            minWidth: 90,
            maxWidth: 90,
            sortable: false,
            filter: false,
            editable: false,
            cellRenderer: (params: ICellRendererParams) => {
                if (!params.data) return null;
                return (
                    <>
                        <IconButton size="small" onClick={() => openEditDialog(params.data.id)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteRow(params.data.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </>
                );
            },
        }),
        [openEditDialog, handleDeleteRow]
    );

    const columnDefs = useMemo(
        () => [...metadataColumns, ...monthColumns, ...(isReadOnly ? [] : [actionColumn])],
        [metadataColumns, monthColumns, actionColumn, isReadOnly]
    );

    const defaultColDef: ColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
        }),
        []
    );

    return (
        <Box className="budget-grid">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                    <span>{t('onTopCosts.title')}</span>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* This grid lives on the Overview and follows the range, so there is no window left to
                        move while one is set — and the Ist-Budget heading up the page already names it. */}
                    <MonthWindowNav rangeBehaviour="hide" />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* CALL-757: export the current on-top list in the import format (round-trips). */}
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => exportCosts()}
                            disabled={isExporting}
                        >
                            <span>{isExporting ? t('export.inProgress') : t('action.export')}</span>
                        </Button>
                        {!isReadOnly && (
                            <ImportSplitButton
                                onImport={() => setShowImportDialog(true)}
                                templatesDisabled={isDownloadingTemplate}
                                templateOptions={[
                                    {
                                        label: t('action.addRecordManually'),
                                        icon: <AddIcon fontSize="small" />,
                                        onClick: openCreateDialog,
                                    },
                                    { label: t('import.downloadSampleTemplate'), onClick: () => downloadTemplate() },
                                ]}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
            <TableGridItem
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onCellValueChanged={handleCellValueChanged}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                cellSelection
                height={'auto'}
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('hint.noOnTopCosts')}</span>}
            />
            <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="sm">
                <DialogTitle>{t('onTopCosts.delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('dialog.deleteOnTopCost')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmId(null)} variant="outlined">
                        {t('action.cancel')}
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        {t('action.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ResponsiveDialog
                open={dialog.mode !== null}
                onClose={resetDialog}
                title={t(dialog.mode === 'edit' ? 'onTopCosts.edit' : 'onTopCosts.add')}
                isConfirmationDialog
                onConfirm={handleDialogConfirm}
                confirmButtonText={t(dialog.mode === 'edit' ? 'action.save' : 'action.create')}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 400 }}>
                    <TextField
                        label={t('field.contactPerson')}
                        value={dialog.contactPerson}
                        onChange={(e) => updateDialogField('contactPerson', e.target.value)}
                        size="small"
                        autoFocus
                    />
                    <TextField
                        label={t('field.responsibleOwner')}
                        value={dialog.owner}
                        onChange={(e) => updateDialogField('owner', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.costCenter')}
                        value={dialog.costCenter}
                        onChange={(e) => updateDialogField('costCenter', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.prName')}
                        value={dialog.prName}
                        onChange={(e) => updateDialogField('prName', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.category')}
                        value={dialog.category}
                        onChange={(e) => updateDialogField('category', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.description')}
                        value={dialog.description}
                        onChange={(e) => updateDialogField('description', e.target.value)}
                        size="small"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dialog.raittFlag}
                                onChange={(e) => updateDialogField('raittFlag', e.target.checked)}
                            />
                        }
                        label={t('field.raittCssOpex')}
                    />
                    <TextField
                        label={t('field.worktype')}
                        value={dialog.worktypeLabel}
                        onChange={(e) => updateDialogField('worktypeLabel', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.channel')}
                        value={dialog.channelLabel}
                        onChange={(e) => updateDialogField('channelLabel', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.supplierComment')}
                        value={dialog.supplier}
                        onChange={(e) => updateDialogField('supplier', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.location')}
                        value={dialog.location}
                        onChange={(e) => updateDialogField('location', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.priceCategory')}
                        value={dialog.priceCategory}
                        onChange={(e) => updateDialogField('priceCategory', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.forecasterWfm')}
                        value={dialog.forecasterWfm}
                        onChange={(e) => updateDialogField('forecasterWfm', e.target.value)}
                        size="small"
                    />
                    <TextField
                        label={t('field.headOfBudget')}
                        value={dialog.headOfBudget}
                        onChange={(e) => updateDialogField('headOfBudget', e.target.value)}
                        size="small"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dialog.prPm}
                                onChange={(e) => updateDialogField('prPm', e.target.checked)}
                            />
                        }
                        label={t('field.prPm')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dialog.includeInBudget}
                                onChange={(e) => updateDialogField('includeInBudget', e.target.checked)}
                            />
                        }
                        label={t('field.includeInBudget')}
                    />
                </Box>
            </ResponsiveDialog>

            <OnTopCostsImportDialog
                open={showImportDialog}
                onClose={() => setShowImportDialog(false)}
                versionId={versionId}
                // CALL-779/757: an import REPLACES the version's whole on-top list, so every row comes back
                // with a new id. Pending edits keyed by the old ids then render nowhere while still being
                // counted and posted — the batch skips the dead ids, resolves 200, and the bar cleared them
                // under a success toast. They cannot survive the replace, so they go with it.
                onImported={() => pending.dropKinds(ON_TOP_EDIT_KINDS)}
            />
        </Box>
    );
};

export default OnTopCostsGrid;
